import React, { useState } from 'react';
import { Key, Copy, Check, RefreshCw } from 'lucide-react';
import { SEO } from '../components/SEO';

interface PasswordOptions {
  length: number;
  includeUppercase: boolean;
  includeLowercase: boolean;
  includeNumbers: boolean;
  includeSymbols: boolean;
  excludeSimilar: boolean;
}

function GeneratorLozinke() {
  const [password, setPassword] = useState<string>('');
  const [copied, setCopied] = useState<boolean>(false);
  const [options, setOptions] = useState<PasswordOptions>({
    length: 16,
    includeUppercase: true,
    includeLowercase: true,
    includeNumbers: true,
    includeSymbols: true,
    excludeSimilar: false
  });

  const characterSets = {
    uppercase: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
    lowercase: 'abcdefghijklmnopqrstuvwxyz',
    numbers: '0123456789',
    symbols: '!@#$%^&*()_+-=[]{}|;:,.<>?',
    similar: 'iIlL1oO0'
  };

  const generatePassword = () => {
    let chars = '';
    if (options.includeUppercase) chars += characterSets.uppercase;
    if (options.includeLowercase) chars += characterSets.lowercase;
    if (options.includeNumbers) chars += characterSets.numbers;
    if (options.includeSymbols) chars += characterSets.symbols;

    if (options.excludeSimilar) {
      characterSets.similar.split('').forEach(char => {
        chars = chars.replace(char, '');
      });
    }

    if (chars.length === 0) {
      setPassword('Odaberite barem jednu opciju');
      return;
    }

    let newPassword = '';
    const array = new Uint32Array(options.length);
    crypto.getRandomValues(array);

    for (let i = 0; i < options.length; i++) {
      newPassword += chars[array[i] % chars.length];
    }

    setPassword(newPassword);
    setCopied(false);
  };

  const copyToClipboard = () => {
    if (password && password !== 'Odaberite barem jednu opciju') {
      navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleOptionChange = (option: keyof PasswordOptions, value: boolean | number) => {
    setOptions(prev => ({
      ...prev,
      [option]: value
    }));
  };

  const calculatePasswordStrength = (password: string): { strength: number; label: string } => {
    if (!password || password === 'Odaberite barem jednu opciju') return { strength: 0, label: 'Nema lozinke' };

    let strength = 0;
    const checks = {
      length: password.length >= 12,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      numbers: /[0-9]/.test(password),
      symbols: /[^A-Za-z0-9]/.test(password),
      noRepeating: !/(.)\1{2,}/.test(password)
    };

    Object.values(checks).forEach(check => {
      if (check) strength += 20;
    });

    strength = Math.min(100, strength);

    let label = '';
    if (strength < 20) label = 'Vrlo slaba';
    else if (strength < 40) label = 'Slaba';
    else if (strength < 60) label = 'Srednja';
    else if (strength < 80) label = 'Jaka';
    else label = 'Vrlo jaka';

    return { strength, label };
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Generator Lozinki",
    "applicationCategory": "UtilityApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Besplatni online generator sigurnih lozinki. Kreirajte jake i jedinstvene lozinke s različitim opcijama prilagodbe.",
    "url": "https://online-kalkulator.com/generator-lozinke",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    }
  };

  const passwordStrength = calculatePasswordStrength(password);

  return (
    <>
      <SEO 
        title="Generator Lozinki | Kreiranje Sigurnih Lozinki Online"
        description="Kreirajte jake i sigurne lozinke online. Besplatni generator s opcijama prilagodbe duljine i znakova."
        canonicalUrl="/generator-lozinke"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Key className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Generator Lozinki</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Generator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Password Display */}
              <div className="relative">
                <input
                  type="text"
                  value={password}
                  readOnly
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg font-mono text-lg"
                  placeholder="Vaša lozinka"
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex space-x-2">
                  <button
                    onClick={generatePassword}
                    className="p-2 text-gray-500 hover:text-blue-600 transition"
                    title="Generiraj novu lozinku"
                  >
                    <RefreshCw className="w-5 h-5" />
                  </button>
                  <button
                    onClick={copyToClipboard}
                    className="p-2 text-gray-500 hover:text-blue-600 transition"
                    title="Kopiraj u međuspremnik"
                  >
                    {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Password Strength Indicator */}
              {password && password !== 'Odaberite barem jednu opciju' && (
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Jačina lozinke:</span>
                    <span className="text-sm font-medium">{passwordStrength.label}</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full transition-all duration-300"
                      style={{ 
                        width: `${passwordStrength.strength}%`,
                        backgroundColor: `hsl(${passwordStrength.strength}, 70%, 45%)`
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Options */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Duljina lozinke: {options.length} znakova
                  </label>
                  <input
                    type="range"
                    min="8"
                    max="64"
                    value={options.length}
                    onChange={(e) => handleOptionChange('length', parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                </div>

                <div className="space-y-2">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={options.includeUppercase}
                      onChange={(e) => handleOptionChange('includeUppercase', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Velika slova (A-Z)</span>
                  </label>

                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={options.includeLowercase}
                      onChange={(e) => handleOptionChange('includeLowercase', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Mala slova (a-z)</span>
                  </label>

                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={options.includeNumbers}
                      onChange={(e) => handleOptionChange('includeNumbers', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Brojevi (0-9)</span>
                  </label>

                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={options.includeSymbols}
                      onChange={(e) => handleOptionChange('includeSymbols', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Posebni znakovi (!@#$%^&*)</span>
                  </label>

                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={options.excludeSimilar}
                      onChange={(e) => handleOptionChange('excludeSimilar', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Izbjegavaj slične znakove (i, l, 1, O, 0)</span>
                  </label>
                </div>

                <button
                  onClick={generatePassword}
                  className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  Generiraj Lozinku
                </button>
              </div>
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Generatoru Lozinki</h2>
                <p className="text-gray-600 leading-relaxed">
                  Generator lozinki je alat koji vam pomaže stvoriti jake i sigurne lozinke za vaše
                  online račune. Koristi kriptografski siguran generator slučajnih brojeva za stvaranje
                  nepredvidivih i jedinstvenih lozinki prema vašim specifikacijama.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Karakteristike Jake Lozinke</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Minimalno 12 znakova duljine</li>
                  <li>Kombinacija velikih i malih slova</li>
                  <li>Uključuje brojeve i posebne znakove</li>
                  <li>Izbjegava predvidljive uzorke</li>
                  <li>Ne sadrži osobne informacije</li>
                  <li>Jedinstvena je za svaki račun</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Odaberite željenu duljinu lozinke</li>
                  <li>Označite vrste znakova koje želite uključiti</li>
                  <li>Kliknite "Generiraj Lozinku" za stvaranje nove lozinke</li>
                  <li>Kopirajte lozinku u međuspremnik klikom na ikonu kopiranja</li>
                  <li>Spremite lozinku na sigurno mjesto</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Savjeti za Sigurnost</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Koristite različite lozinke za različite račune</li>
                  <li>Redovito mijenjajte važne lozinke</li>
                  <li>Nikad ne dijelite svoje lozinke</li>
                  <li>Koristite upravitelj lozinki za sigurno pohranjivanje</li>
                  <li>Uključite dvofaktorsku autentifikaciju gdje je moguće</li>
                  <li>Izbjegavajte spremanje lozinki u običnim tekstualnim datotekama</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Jačina Lozinke</h2>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-16 h-2 bg-red-500 rounded"></div>
                    <span className="text-gray-600">Vrlo slaba: Samo jedna vrsta znakova</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-16 h-2 bg-orange-500 rounded"></div>
                    <span className="text-gray-600">Slaba: Dvije vrste znakova</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-16 h-2 bg-yellow-500 rounded"></div>
                    <span className="text-gray-600">Srednja: Tri vrste znakova</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-16 h-2 bg-green-500 rounded"></div>
                    <span className="text-gray-600">Jaka: Sve vrste znakova, dulja od 12 znakova</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-16 h-2 bg-blue-500 rounded"></div>
                    <span className="text-gray-600">Vrlo jaka: Sve vrste znakova, dulja od 16 znakova</span>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default GeneratorLozinke;